/**
 * Choix des categories d'images jouees.
 *
 * Une categorie ne se DECRIT pas, elle se montre : « Grille » ou « Dense » ne
 * veulent rien dire tant qu'on n'a pas vu de quoi il retourne. Chaque case
 * porte donc un tableau de reference, et le mot n'est qu'une etiquette pour le
 * nommer. C'est aussi ce qui garde le bloc compact : cinq vignettes tiennent
 * sur deux rangees d'un telephone, la ou cinq phrases prendraient l'ecran.
 *
 * Deux cas ou une case ne se decoche pas, et chacun dit pourquoi au survol :
 *  - c'est la DERNIERE cochee : une partie sans categorie n'a aucun tableau a
 *    tirer. Le protocole refuse deja le vide, l'interface refuse le geste ;
 *  - la categorie n'a AUCUN tableau pret (image ajoutee, zones pas encore
 *    tracees). Le serveur la refuserait, autant ne pas la proposer.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { CATEGORIES, type Category } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import type { TranslationKey } from '../locales/index.js';
import { loadArtwork } from '../game/artwork.js';
import { CheckIcon } from './icons.js';

/** Libelles, dans l'ordre des prefixes de fichiers. */
const CATEGORY_LABEL: Record<number, TranslationKey> = {
  1: 'category.1',
  2: 'category.2',
  3: 'category.3',
  4: 'category.4',
  5: 'category.5',
  6: 'category.6',
  7: 'category.7',
};

/**
 * Tableau qui illustre chaque categorie. Choisi a la main : c'est celui qui
 * montre le mieux la facon de peindre, pas le premier de la serie.
 */
const CATEGORY_SAMPLE: Record<number, string> = {
  1: '1_snow',
  2: '2_plants',
  3: '3_shelf',
  4: '4_plants',
  5: '5_boat',
  6: '6_farm',
  7: '7_japan',
};

/**
 * URL reelle de l'image, extension comprise.
 *
 * On passe par loadArtwork plutot que d'ecrire « .webp » en dur : c'est lui qui
 * essaie les extensions dans l'ordre, et le code ne doit jamais dependre du
 * format (placeholders SVG en dev, WebP en prod). Effet de bord utile, l'image
 * est alors deja en cache quand la partie commence.
 */
function useArtworkUrl(file: string): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    loadArtwork(file)
      .then((image) => {
        if (alive) setUrl(image.src);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [file]);

  return url;
}

export function CategoryPicker({
  selected,
  available,
  disabled,
  onChange,
}: {
  selected: readonly number[];
  /** Categories que le serveur peut servir. Les autres sont montrees, eteintes. */
  available: readonly number[];
  disabled: boolean;
  onChange: (categories: Category[]) => void;
}): ReactElement {
  const t = useT();
  const last = selected.length <= 1;

  return (
    <div className="segmented">
      <p className="section-label">{t('lobby.categories')}</p>
      <div className="categories" role="group" aria-label={t('lobby.categories')}>
        {CATEGORIES.map((category) => {
          const on = selected.includes(category);
          const empty = !available.includes(category);
          // Une case cochee qui serait la derniere reste cochee : on bloque le
          // geste plutot que de laisser partir un message que le serveur
          // refusera.
          const locked = empty || (on && last);
          const reason = empty
            ? t('lobby.categoryEmpty')
            : on && last
              ? t('lobby.categoryLast')
              : undefined;

          return (
            <CategoryTile
              key={category}
              category={category}
              label={t(CATEGORY_LABEL[category] as TranslationKey)}
              selected={on}
              disabled={disabled || locked}
              {...(reason === undefined ? {} : { reason })}
              // Reconstruite depuis CATEGORIES : la liste sort triee, sans
              // doublon et correctement typee, sans avoir a la retrier.
              onToggle={() =>
                onChange(
                  CATEGORIES.filter((value) =>
                    value === category ? !on : selected.includes(value),
                  ),
                )
              }
            />
          );
        })}
      </div>
      <p className="segmented__hint">{t('lobby.categoriesHint')}</p>
    </div>
  );
}

function CategoryTile({
  category,
  label,
  selected,
  disabled,
  reason,
  onToggle,
}: {
  category: number;
  label: string;
  selected: boolean;
  disabled: boolean;
  reason?: string;
  onToggle: () => void;
}): ReactElement {
  const url = useArtworkUrl(CATEGORY_SAMPLE[category] as string);

  return (
    <button
      type="button"
      className="category"
      // Une case a cocher, pas un choix exclusif : aria-pressed dirait « ce
      // bouton est enfonce », checkbox dit « cette categorie est retenue ».
      role="checkbox"
      aria-checked={selected}
      aria-label={label}
      disabled={disabled}
      {...(reason === undefined ? {} : { title: reason })}
      onClick={onToggle}
    >
      <span className="category__frame">
        {url !== null && <img className="category__image" src={url} alt="" loading="lazy" />}
        {selected && (
          <span className="category__check" aria-hidden="true">
            <CheckIcon size={14} />
          </span>
        )}
      </span>
      <span className="category__label">{label}</span>
    </button>
  );
}
